import { Crown, Hexagon, SquareEqual, SquareMinus, SquarePlus } from 'lucide-react'
import { FC, useEffect, useState } from 'react'

import { DatabaseGame } from 'features/firebase/types'
import { getPlayerGames } from 'features/firebase/user-operations'
import { useAuthenticatedUser } from 'features/user/auth-store'
import { getGamesStats } from 'features/user/utils'

import { Card } from 'shared/components/ui/card'
import { Separator } from 'shared/components/ui/separator'

export const PlayerStats: FC = () => {
  const { user } = useAuthenticatedUser()
  const [games, setGames] = useState<DatabaseGame[]>([])

  const {
    winCount,
    drawCount,
    loseCount,
    drawPercentage,
    losePercentage,
    totalGames,
    winPercentage,
    blackWinPercentage,
    whiteWinPercentage,
    blackWins,
    whiteWins,
  } = getGamesStats(user.uid, games)

  useEffect(() => {
    getPlayerGames(user.uid).then(res => setGames(res))
  }, [user.uid])

  return (
    <Card className="mx-auto my-10 flex w-11/12 flex-col items-center gap-2 p-6 sm:gap-6 lg:w-[800px]">
      <div className="space-y-2 text-center">
        <span className="text-md font-semibold text-gray-500">Games</span>
        <div className="flex items-center gap-2 text-4xl font-bold">
          <Hexagon size={40} />
          {totalGames}
        </div>
      </div>

      <Separator />

      <div className="w-full space-y-2 text-center">
        <div className="flex justify-around text-lg font-bold">
          <div className="flex items-center space-x-1 text-green-600">
            <SquarePlus />
            <span>{winPercentage.toFixed(0)}%</span>
          </div>
          <div className="flex items-center space-x-1 text-gray-600">
            <SquareEqual />
            <span>{drawPercentage.toFixed(0)}%</span>
          </div>
          <div className="flex items-center space-x-1 text-red-600">
            <SquareMinus />
            <span>{losePercentage.toFixed(0)}%</span>
          </div>
        </div>

        <div className="relative h-4 w-full overflow-hidden rounded-full bg-gray-200">
          <div style={{ width: `${winPercentage}%` }} className="absolute left-0 h-full bg-green-600" />
          <div
            style={{ width: `${drawPercentage}%`, left: `${winPercentage}%` }}
            className="absolute h-full bg-gray-600"
          />
          <div
            style={{ width: `${losePercentage}%`, left: `${winPercentage + drawPercentage}%` }}
            className="absolute h-full bg-red-600"
          />
        </div>

        <div className="text-md flex justify-around">
          <div className="flex items-center space-x-1 text-green-600">
            <span>{winCount}</span>
            <span>Won</span>
          </div>
          <div className="flex items-center space-x-1 text-gray-600">
            <span>{drawCount}</span>
            <span>Drawn</span>
          </div>
          <div className="flex items-center space-x-1 text-red-600">
            <span>{loseCount}</span>
            <span>Lost</span>
          </div>
        </div>
      </div>

      <Separator />
      <span className="text-md flex gap-1 font-semibold text-gray-500">
        <Crown />
        Winning with
      </span>
      <div className="w-full space-y-4 text-center">
        <div className="flex justify-around text-lg font-bold">
          <div className="flex items-center space-x-1 text-gray-400">
            <span>{whiteWinPercentage.toFixed(0)}%</span>
          </div>

          <div className="flex items-center space-x-1 text-black">
            <span>{blackWinPercentage.toFixed(0)}%</span>
          </div>
        </div>

        <div className="relative h-4 w-full overflow-hidden rounded-full bg-gray-400">
          <div style={{ width: `${whiteWinPercentage}%` }} className="absolute left-0 h-full bg-green-600"></div>
          <div
            style={{ width: `${blackWinPercentage}%`, left: `${whiteWinPercentage}%` }}
            className="absolute h-full bg-black"
          ></div>
        </div>

        <div className="flex justify-around text-sm">
          <div className="flex items-center space-x-1 text-gray-400">
            <span>{whiteWins}</span>
            <span>White Wins</span>
          </div>
          <div className="flex items-center space-x-1 text-black">
            <span>{blackWins}</span>
            <span>Black Wins</span>
          </div>
        </div>
      </div>
    </Card>
  )
}
